import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Settings,
  UserPlus,
  LogOut,
  ChevronRight,
  Mail,
  FileText,
  Shield,
  MoreHorizontal,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useSession } from "@/App";
import BrandLogo from "@/components/BrandLogo";

/**
 * Floating settings/⋯ button visible only on mobile.
 * Opens a Sheet with secondary navigation items (Impostazioni, Invita, Logout, etc.).
 * Desktop already has these in the sidebar so we hide this component with md:hidden.
 */
export default function MobileMoreMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, family, logout } = useSession();

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const onLogout = () => {
    setOpen(false);
    logout();
    navigate("/", { replace: true });
  };

  const items = [
    {
      label: "Impostazioni",
      description: "Profilo, famiglia, notifiche, piano",
      icon: Settings,
      onClick: () => go("/settings"),
      testid: "mobile-menu-settings",
    },
    {
      label: "Invita membri",
      description: "Aggiungi partner, figli o parenti",
      icon: UserPlus,
      onClick: () => go("/settings#invite"),
      testid: "mobile-menu-invite",
    },
    {
      label: "Privacy",
      description: "Come trattiamo i tuoi dati",
      icon: Shield,
      onClick: () => go("/privacy"),
      testid: "mobile-menu-privacy",
    },
    {
      label: "Termini di servizio",
      description: "Condizioni d'uso",
      icon: FileText,
      onClick: () => go("/terms"),
      testid: "mobile-menu-terms",
    },
    {
      label: "Contattaci",
      description: "Assistenza e feedback",
      icon: Mail,
      onClick: () => (window.location.href = "mailto:supporto@familybrain.it"),
      testid: "mobile-menu-contact",
    },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="mobile-more-button"
          aria-label="Apri menu"
          className="md:hidden fixed top-3 right-3 z-40 h-11 w-11 grid place-items-center rounded-full glass-panel border border-subtle shadow-lg active:scale-95 transition"
        >
          <MoreHorizontal className="w-5 h-5 text-primary-fg" strokeWidth={1.8} />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[85%] max-w-sm p-0 flex flex-col bg-app">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-subtle text-left">
          <div className="flex items-center gap-3">
            <BrandLogo size={36} />
            <div>
              <SheetTitle className="text-base font-display">Altro</SheetTitle>
              <div className="text-xs text-tertiary-fg mt-0.5 truncate">
                {user?.name} · {family?.name}
              </div>
            </div>
          </div>
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto py-2">
          {items.map(({ label, description, icon: Icon, onClick, testid }) => (
            <button
              key={label}
              type="button"
              onClick={onClick}
              data-testid={testid}
              className="w-full flex items-center gap-4 px-6 py-4 hover:bg-elevated active:bg-elevated transition-colors text-left"
            >
              <div className="h-10 w-10 rounded-xl bg-elevated grid place-items-center shrink-0">
                <Icon className="w-4.5 h-4.5 text-secondary-fg" strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-primary-fg">{label}</div>
                <div className="text-xs text-tertiary-fg mt-0.5 truncate">{description}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-tertiary-fg shrink-0" />
            </button>
          ))}
        </nav>

        <div className="px-6 py-4 border-t border-subtle">
          <button
            type="button"
            onClick={onLogout}
            data-testid="mobile-menu-logout"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-subtle text-sm font-medium text-primary-fg hover:bg-elevated active:bg-elevated transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.8} />
            Esci dall&apos;account
          </button>
        </div>

        <Link
          to="/settings"
          onClick={() => setOpen(false)}
          className="text-center text-[11px] uppercase tracking-widest text-tertiary-fg pb-5 hover:text-secondary-fg"
        >
          v1 · familybrain.it
        </Link>
      </SheetContent>
    </Sheet>
  );
}
